import React, { useState } from 'react';
import { sendInquiryEmail } from '../utils/inquiryEmail';
import * as inquiry from './inquirySubmission';
import CountryCodeSelector from './CountryCodeSelector';


const isValidEmail = (email) => {
  const value = (email || '').trim();
  if (!value) return true;
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i.test(value);
};

const digitsOnly = (value) => (value || '').replace(/\D/g, '');

const preventNonNumeric = (event) => {
  const allowedKeys = [
    'Backspace',
    'Delete',
    'Tab',
    'Escape',
    'Enter',
    'ArrowLeft',
    'ArrowRight',
    'Home',
    'End',
  ];

  if (event.ctrlKey || event.metaKey) return;
  if (allowedKeys.includes(event.key)) return;
  if (!/^\d$/.test(event.key)) event.preventDefault();
};

const ContactProjectSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    countryCode: inquiry.DEFAULT_COUNTRY_CODE,
    phone: '',
    service: '',
    details: '',
  });
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState({ success: '', error: '' });

  // Telegram submission kept for future use. Email submission is active now.
  // const botToken = '7869975126:AAETFptfICnYosInsBlMFWlfOpeeGAl_s10';
  // const chatId = '-1002662497260';

  const handleChange = (event) => {
    const { name, value } = event.target;

    if (name === 'countryCode') {
      const rule = inquiry.getCountryPhoneRule(value);
      setFormData((prev) => ({
        ...prev,
        countryCode: value,
        phone: digitsOnly(prev.phone).slice(0, rule.maxDigits),
      }));
      return;
    }

    if (name === 'phone') {
      const code = formData.countryCode || inquiry.DEFAULT_COUNTRY_CODE;
      const rule = inquiry.getCountryPhoneRule(code);
      setFormData((prev) => ({
        ...prev,
        phone: digitsOnly(value).slice(0, rule.maxDigits),
      }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setStatus({ success: '', error: '' });

    const code = formData.countryCode || inquiry.DEFAULT_COUNTRY_CODE;
    const phoneDigits = digitsOnly(formData.phone);

    if (!phoneDigits) {
      setStatus({ success: '', error: 'Please enter your mobile number.' });
      setLoading(false);
      return;
    }

    if (!inquiry.isValidPhoneNumber(code, phoneDigits)) {
      setStatus({
        success: '',
        error: inquiry.getPhoneValidationMessage(code),
      });
      setLoading(false);
      return;
    }

    if (!formData.details.trim()) {
      setStatus({ success: '', error: 'Please enter your message.' });
      setLoading(false);
      return;
    }

    if (!isValidEmail(formData.email)) {
      setStatus({ success: '', error: 'Please enter a valid email address.' });
      setLoading(false);
      return;
    }

    const fullPhone = `${inquiry.getDialCode(code)}${phoneDigits}`;

    try {
      await sendInquiryEmail({
        formName: 'SwiftMaze Home Page About Form',
        name: formData.name,
        email: formData.email,
        phone: fullPhone,
        service: formData.service,
        message: formData.details,
      });

      // Telegram submission kept for future use. Email submission is active now.
      // await fetch(`https://api.telegram.org/bot${botToken}/sendMessage`, {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify({ chat_id: chatId, text: msg, parse_mode: 'Markdown' }),
      // });

      setStatus({
        success: 'Thank you! Your inquiry has been received. We will get back to you shortly.',
        error: '',
      });
      setFormData({ name: '', email: '', countryCode: inquiry.DEFAULT_COUNTRY_CODE, phone: '', service: '', details: '' });
    } catch (error) {
      console.error('Error sending inquiry email:', error);
      setStatus({
        success: '',
        error: 'Something went wrong. Please check your internet connection and try again later.',
      });
    }

    setLoading(false);
  };

  return (
    <section id="project-contact" className="project-contact-section">
      <div className="auto-container">
        <div className="row clearfix">
          <div className="info-column col-lg-6 col-md-12 col-sm-12">
            <div className="inner-column">
              <div className="section-heading">
                <h2 className="section-title">Let’s Talk About Your Project</h2>
              </div>

              <div className="soc-box">
                <a href="tel:+919978623133">
                  <i className="fa fa-phone"></i>
                  <span>Book A Call</span>
                </a>
                <a href="https://wa.me/9978623133" target="_blank" rel="noreferrer">
                  <i className="fa fa-whatsapp"></i>
                  <span>WhatsApp</span>
                </a>
                <a href="mailto:swiftmazetech@gmail.com">
                  <i className="fa fa-envelope"></i>
                  <span>Email Us</span>
                </a>
              </div>

              <div className="experience-content">
                <div className="single-experience-box">
                  <div className="icon">
                    <span className="icon-badge">★</span>
                  </div>
                  <div className="content">
                    <h5>10+ years of steady work</h5>
                    <p>
                      We have been building websites, apps, and software for more than
                      ten years. Our team has handled many projects for small and
                      growing businesses. Each project is planned with care so you feel
                      safe and clear at every step.
                    </p>
                  </div>
                </div>

                <div className="single-experience-box">
                  <div className="icon">
                    <span className="icon-badge">👍</span>
                  </div>
                  <div className="content">
                    <h5>100+ happy clients</h5>
                    <p>
                      More than one hundred clients have trusted us with their online work.
                      Their kind feedback shows the value we bring through simple and
                      steady service. We focus on clean results that help your business
                      move ahead.
                    </p>
                  </div>
                </div>

                <div className="single-experience-box">
                  <div className="icon">
                    <span className="icon-badge">👨‍💻</span>
                  </div>
                  <div className="content">
                    <h5>Skilled in-house team</h5>
                    <p>
                      Our in-house team works together under one roof to plan, build,
                      and support your project. You get a mix of designers, developers,
                      and support staff who know how to keep things simple and
                      organized. This helps us deliver strong work on time.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="form-column col-lg-6 col-md-12 col-sm-12">
            <div className="inner-column">
              <div className="discuss-project-form">
                <div className="project-form row clearfix">
                  <div className="form-group col-md-6 col-sm-12">
                    <label>Your Name</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Enter Your Name"
                    />
                    <span className="focus-underline"></span>
                  </div>

                  <div className="form-group col-md-6 col-sm-12">
                    <label>Phone Number <span>*</span></label>
                    <div className="project-contact-phone-row">
                      <CountryCodeSelector
                        value={formData.countryCode}
                        onChange={(countryCode) => handleChange({ target: { name: 'countryCode', value: countryCode } })}
                      />
                      <input
                        type="tel"
                        name="phone"
                        inputMode="numeric"
                        autoComplete="tel"
                        placeholder={
                          inquiry.getCountryPhoneRule(formData.countryCode).placeholder
                        }
                        value={formData.phone}
                        onChange={handleChange}
                        onKeyDown={preventNonNumeric}
                        maxLength={inquiry.getCountryPhoneRule(formData.countryCode).maxDigits}
                        required
                        style={{ flex: 1 }}
                      />
                    </div>
                    <span className="focus-underline"></span>
                  </div>

                  <div className="form-group col-md-6 col-sm-12">
                    <label>Your Email</label>
                    <input
                      type="email"
                      name="email"
                      placeholder="Enter Your Email"
                      value={formData.email}
                      onChange={handleChange}
                      autoComplete="email"
                    />
                    <span className="focus-underline"></span>
                  </div>

                  <div className="form-group col-md-6 col-sm-12">
                    <label>Services</label>
                    <select name="service" value={formData.service} onChange={handleChange}>
                      <option value="">Select Your Services</option>
                      <option value="website">Website Development</option>
                      <option value="software">Software Development</option>
                      <option value="mobile">Mobile App Development</option>
                      <option value="marketing">Digital Marketing</option>
                      <option value="bacainternship">BCA Internship & Industrial Project Guidance</option>
                      <option value="industryproject">Industry Project, Internship & Mentorship</option>
                      <option value="projectguidance">Internship & Project Guidance (Modasa)</option>
                      <option value="btechbe">BTech / BE Internship Project Guidance</option>
                      <option value="other">Other</option>
                    </select>
                    <span className="focus-underline"></span>
                  </div>

                  <div className="form-group col-md-12 col-sm-12">
                    <label>Project Details <span>*</span></label>
                    <textarea
                      name="details"
                      rows="4"
                      placeholder="Please enter project details"
                      value={formData.details}
                      onChange={handleChange}
                      required
                    ></textarea>
                    <span className="focus-underline"></span>
                  </div>

                  {status.success && (
                    <div className="form-group col-md-12">
                      <p className="text-success">{status.success}</p>
                    </div>
                  )}
                  {status.error && (
                    <div className="form-group col-md-12">
                      <p className="text-danger">{status.error}</p>
                    </div>
                  )}

                  <div className="form-group col-md-12 col-sm-12">
                    <button type="submit" className="theme-btn btn-style-one" disabled={loading}>
                      <span className="txt">{loading ? 'Sending...' : 'Submit Project Details'}</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default ContactProjectSection;
